import React from 'react';
import { motion } from 'framer-motion';
interface VerbTableProps {
  verb: string;
  tense: 'present' | 'past' | 'perfect';
  persons: string[];
  forms: string[];
  highlight?: string;
  label?: string;
  /** 'stimulus' = compact visual only (shown in-question), 'support' = full educational panel (shown after answer) */
  mode?: 'stimulus' | 'support';
}
/**
 * VerbTable - Dutch verb conjugation table visual
 *
 * Shows verb conjugation patterns with educational explanations.
 * Used for SWW (Spelling Werkwoorden) module questions.
 *
 * stimulus mode: compact — verb info + conjugation table only
 * support mode: full educational panel with explanations and tips
 */
export function VerbTable({
  verb,
  tense,
  persons,
  forms,
  highlight,
  label,
  mode = 'support'
}: VerbTableProps) {
  // Extract stem (remove -en ending)
  const stem = verb.endsWith('en') ? verb.slice(0, -2) : verb;
  // Tense labels
  const tenseLabels = {
    present: 'Tegenwoordige Tijd',
    past: 'Verleden Tijd',
    perfect: 'Voltooid Deelwoord'
  };
  // ── STIMULUS MODE: compact — verb + table only ──────────────────────────
  if (mode === 'stimulus') {
    return <div className="w-full max-w-md mx-auto" data-id="element-10334">
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-200" data-id="element-10335">
          {/* Header - compact */}
          {label && <div className="mb-3 text-center" data-id="element-10336">
              <span className="text-xs font-bold text-indigo-800" data-id="element-10337">{label}</span>
            </div>}

          {/* Verb Info - compact */}
          <div className="mb-3 flex items-center justify-center gap-3" data-id="element-10338">
            <div className="text-center" data-id="element-10339">
              <div className="text-[10px] font-bold text-slate-500 uppercase" data-id="element-10340">
                Werkwoord
              </div>
              <div className="text-lg font-black text-indigo-700" data-id="element-10341">{verb}</div>
            </div>
            <div className="text-lg text-slate-300" data-id="element-10342">→</div>
            <div className="text-center" data-id="element-10343">
              <div className="text-[10px] font-bold text-slate-500 uppercase" data-id="element-10344">
                Stam
              </div>
              <div className="text-lg font-black text-purple-700" data-id="element-10345">{stem}</div>
            </div>
          </div>

          {/* Conjugation Table - compact */}
          <div className="bg-white/90 rounded-lg overflow-hidden border border-indigo-200" data-id="element-10346">
            <table className="w-full text-sm" data-id="element-10347">
              <thead data-id="element-10348">
                <tr className="bg-indigo-100/70" data-id="element-10349">
                  <th className="px-3 py-2 text-left text-xs font-bold text-indigo-900" data-id="element-10350">
                    Persoon
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-indigo-900" data-id="element-10351">
                    Vorm
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-10352">
                {persons.map((person, index) => {
                const form = forms[index];
                const isHighlighted = highlight === person || highlight === form;
                return <motion.tr key={person} initial={{
                  opacity: 0,
                  x: -5
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: index * 0.06
                }} className={`border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-indigo-50' : ''}`} data-id="element-10353">
                      <td className="px-3 py-1.5" data-id="element-10354">
                        <span className={`font-semibold text-xs ${isHighlighted ? 'text-indigo-700' : 'text-slate-600'}`} data-id="element-10355">
                          {person}
                        </span>
                      </td>
                      <td className="px-3 py-1.5" data-id="element-10356">
                        <span className={`font-bold ${isHighlighted ? 'text-indigo-700' : 'text-slate-800'}`} data-id="element-10357">
                          {form}
                        </span>
                      </td>
                    </motion.tr>;
              })}
              </tbody>
            </table>
          </div>
        </div>
      </div>;
  }
  // ── SUPPORT MODE: full educational panel (original behavior) ────────────
  return <div className="w-full max-w-3xl mx-auto" data-id="element-10358">
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 md:p-8 border-2 border-indigo-200 shadow-lg" data-id="element-10359">
        {/* Header */}
        <div className="mb-6 text-center" data-id="element-10360">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-indigo-200 shadow-sm" data-id="element-10361">
            <span className="text-2xl" data-id="element-10362">📝</span>
            <div className="text-left" data-id="element-10363">
              <div className="text-sm font-bold text-indigo-900" data-id="element-10364">
                {label || 'Werkwoord Vervoegen'}
              </div>
              <div className="text-xs text-indigo-600 font-semibold" data-id="element-10365">
                {tenseLabels[tense]}
              </div>
            </div>
          </div>
        </div>

        {/* Verb Info */}
        <div className="mb-6 bg-white/80 rounded-xl p-4 border border-indigo-100" data-id="element-10366">
          <div className="flex items-center justify-center gap-4 flex-wrap" data-id="element-10367">
            <div className="text-center" data-id="element-10368">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-10369">
                Werkwoord
              </div>
              <div className="text-2xl font-black text-indigo-700" data-id="element-10370">{verb}</div>
            </div>
            <div className="text-2xl text-slate-300" data-id="element-10371">→</div>
            <div className="text-center" data-id="element-10372">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-10373">
                Stam
              </div>
              <div className="text-2xl font-black text-purple-700" data-id="element-10374">{stem}</div>
            </div>
          </div>
        </div>

        {/* Conjugation Table */}
        <div className="bg-white/90 rounded-xl overflow-hidden border border-indigo-200 shadow-sm mb-6" data-id="element-10375">
          <table className="w-full" data-id="element-10376">
            <thead data-id="element-10377">
              <tr className="bg-indigo-100 border-b border-indigo-200" data-id="element-10378">
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-10379">
                  Persoon
                </th>
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-10380">
                  Vorm
                </th>
              </tr>
            </thead>
            <tbody data-id="element-10381">
              {persons.map((person, index) => {
              const form = forms[index];
              const isHighlighted = highlight === person || highlight === form;
              return <motion.tr key={person} initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.1
              }} className={`border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-indigo-50' : 'hover:bg-slate-50'}`} data-id="element-10382">
                    <td className="px-4 py-3" data-id="element-10383">
                      <span className={`font-bold ${isHighlighted ? 'text-indigo-700' : 'text-slate-700'}`} data-id="element-10384">
                        {person}
                      </span>
                    </td>
                    <td className="px-4 py-3" data-id="element-10385">
                      <span className={`font-bold text-lg ${isHighlighted ? 'text-indigo-700' : 'text-slate-800'}`} data-id="element-10386">
                        {form}
                      </span>
                    </td>
                  </motion.tr>;
            })}
            </tbody>
          </table>
        </div>

        {/* Educational Explanations */}
        <div className="space-y-4" data-id="element-10387">
          {/* How to find the stem */}
          <div className="bg-white/80 rounded-xl p-5 border border-blue-100 shadow-sm" data-id="element-10388">
            <div className="flex items-start gap-3" data-id="element-10389">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-10390">
                <span className="text-blue-600 text-lg" data-id="element-10391">🔍</span>
              </div>
              <div data-id="element-10392">
                <h4 className="text-sm font-bold text-blue-900 mb-2" data-id="element-10393">
                  Hoe vind je de stam?
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-10394">
                  De <strong data-id="element-10395">stam</strong> krijg je door <strong data-id="element-10396">-en</strong>{' '}
                  weg te halen van het hele werkwoord.{' '}
                  <span className="font-mono bg-blue-50 px-2 py-0.5 rounded border border-blue-200" data-id="element-10397">
                    {verb}
                  </span>{' '}
                  →{' '}
                  <span className="font-mono bg-purple-50 px-2 py-0.5 rounded border border-purple-200" data-id="element-10398">
                    {stem}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Conjugation pattern */}
          {tense === 'present' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-10399">
              <div className="flex items-start gap-3" data-id="element-10400">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-10401">
                  <span className="text-emerald-600 text-lg" data-id="element-10402">📋</span>
                </div>
                <div data-id="element-10403">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-10404">
                    Patroon Tegenwoordige Tijd
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-10405">
                    <p data-id="element-10406">
                      <strong data-id="element-10407">ik:</strong> stam (geen -t) →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-10408">
                        {stem}
                      </span>
                    </p>
                    <p data-id="element-10409">
                      <strong data-id="element-10410">jij/je, u:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-10411">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-10412">
                      <strong data-id="element-10413">hij/zij/het:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-10414">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-10415">
                      <strong data-id="element-10416">wij/jullie/zij:</strong> hele werkwoord →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-10417">
                        {verb}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {/* Tip */}
          <div className="bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-10418">
            <div className="flex items-start gap-3" data-id="element-10419">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-10420">
                <span className="text-amber-600 text-lg" data-id="element-10421">💡</span>
              </div>
              <div data-id="element-10422">
                <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-10423">
                  Handige tip
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-10424">
                  Bij <strong data-id="element-10425">inversie</strong> (als 'jij' achter het werkwoord
                  staat) valt de -t weg: <em data-id="element-10426">jij loopt</em> wordt{' '}
                  <em data-id="element-10427">loop jij?</em>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}